import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
    Box,
    CircularProgress,
} from '@mui/material';

import {
    DataGridPro,
    GridToolbar,
    GridColDef,
    useGridApiRef,
    gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid-pro';

import dayjs from 'dayjs';

import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import { socket } from '../services/socket';

interface InvoiceRow {
    id: string;
    invoiceId: string;
    invoiceDate: string;
    customerName: string;
    eventName: string;
    eventDate: string;
    venueName: string;
    section: string;
    row: string;
    seats: string;
    qty: number;
    costPerTicket: number;
    totalCost: number;
    invoiceTotal: number;
    feeRevenue: string;
    feeRevenueAmount: number;
    feeProfit: string;
    feeProfitAmount: number;
    //payoutProfit: number;
    payout: number;
    delivery: string;
    ticketType: string;
    paymentStatus: string;
    payoutDate: string;
}

interface OutletContextType {
    isConnected: boolean;
    current_broker: string;
}

const api = axios.create({
    withCredentials: true
});

const InvoiceReport: React.FC = () => {
    const { isConnected, current_broker } =
        useOutletContext<OutletContextType>();
    
    const apiRef = useGridApiRef();

    const { getAccessTokenSilently } = useAuth0();

    const [invoiceRows, setInvoiceRows] = useState<InvoiceRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [footerTotals, setFooterTotals] = useState({
        totalCost: 0,
        invoiceTotal: 0,
        payout: 0,
        feeRevenue: 0,
        feeProfit: 0,
    });

    const dateFormatter = (value: string | null | undefined) =>
        value ? dayjs(value).format('MM/DD/YYYY h:mmA') : '';

    const percentageFormatter = (value: number | null | undefined) => {
        if (value === null || value === undefined) return '';
        return `${Math.round(value * 100)}%`;
    };

    const currencyFormatter = (value: number | null | undefined) => {
        if (value == null) {
            return '';
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    };

    const calculateFooterTotals = () => {
        if (!apiRef.current) return;

        const rows = gridFilteredSortedRowEntriesSelector(apiRef);

        const totals = rows.reduce(
            (acc, { model }) => {
                const row = model as InvoiceRow;

                acc.totalCost += row.totalCost ?? 0;
                acc.invoiceTotal += row.invoiceTotal ?? 0;
                acc.payout += row.payout ?? 0;
                acc.feeRevenue += row.feeRevenueAmount ?? 0;
                acc.feeProfit += row.feeProfitAmount ?? 0;

                return acc;
            },
            {
                totalCost: 0,
                invoiceTotal: 0,
                payout: 0,
                feeRevenue: 0,
                feeProfit: 0,
            }
        );

        setFooterTotals(totals);
    };

    async function getCommissionsReportData() {
        try {
            setIsLoading(true);

            const brokerIds = current_broker !== 'all' ? Number.parseInt(current_broker, 10) : undefined; 

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const response = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProReport/invoices`,
                {
                    params: {
                        brokerIds: brokerIds
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );
            
            const r: InvoiceRow[] = response.data.map((invoice: any) => {
                const ticketGroups = invoice.ticketGroups ?? [];
                const tickets = invoice.tickets ?? [];
                const commissions = invoice.commissions ?? [];
                const customers = invoice.customers ?? [];

                const ticketKey = tickets.map((t: any) => t.ticketId).sort().join("-");

                return {
                    id: `${invoice.invoiceSourceId}-${invoice.invoiceId}-${ticketKey}`,
                    invoiceId: invoice.invoiceSourceId.toString(),
                    invoiceDate: invoice.invoiceDate,
                    customerName: [...new Set(customers.map((cu: any) => cu.customerName))].join(", "),
                    eventName: invoice.eventName,
                    eventDate: invoice.eventDate,
                    venueName: invoice.venueName,
                    section: [...new Set(tickets.map((t: any) => t.section))].join(", "),
                    row: [...new Set(tickets.map((t: any) => t.row))].join(", "),
                    seats: tickets.map((t: any) => t.seatNumber).join(", "),
                    qty: tickets.length,
                    costPerTicket: invoice.averageTicketCost,
                    totalCost: invoice.totalTicketCost,
                    invoiceTotal: invoice.invoiceTotal,
                    feeProfit:
                        invoice.recentProfitFeeAmount == null &&
                        invoice.recentProfitFeePercentage == null ||
                        invoice.recentProfitFeeAmount == 0
                            ? ''
                            : `${currencyFormatter(invoice.recentProfitFeeAmount)} @ ${percentageFormatter(invoice.recentProfitFeePercentage)}`,

                    feeProfitAmount: invoice.recentProfitFeeAmount ?? 0,

                    feeRevenue:
                        invoice.recentRevenueFeeAmount == null &&
                        invoice.recentRevenueFeePercentage == null
                            ? ''
                            : `${currencyFormatter(invoice.recentRevenueFeeAmount)} @ ${percentageFormatter(invoice.recentRevenueFeePercentage)}`,

                    feeRevenueAmount: invoice.recentRevenueFeeAmount ?? 0,
                    //payoutProfit: invoice.payoutProfit,
                    payout: invoice.payoutTotal,
                    payoutDate: [...new Set(
                        commissions
                            .map((c: any) => c.payoutPaymentPayDate)
                            .filter(Boolean)
                    )].join(", "),    
                    delivery: invoice.shippingTrackingStatus ?? "",
                    ticketType: [...new Set(
                        ticketGroups
                            .map((t: any) => t.ticketGroupStockType)
                            .filter(Boolean)
                    )].join(", "),    
                    paymentStatus: [...new Set(
                        commissions
                            .map((c: any) => c.payoutPaymentStatus)
                            .filter(Boolean)
                    )].join(", "), 
                };
            });

            setInvoiceRows(r);

        } catch (err: any) {
            if (err.message !== 'canceled') {
                toast.error(
                    err?.response?.data?.message || 'Failed to fetch commissions report data.'
                );
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isConnected) {
            getCommissionsReportData();
            socket.on('commissions', getCommissionsReportData);
        }

        return () => {
            socket.off('commissions', getCommissionsReportData);
        };
    }, [isConnected]);

    useEffect(() => {
        if (invoiceRows.length) {
            queueMicrotask(calculateFooterTotals);
        }
    }, [invoiceRows]);

    const columns = useMemo<GridColDef<InvoiceRow>[]>(
        () => [
            { field: 'invoiceId', headerName: 'Invoice ID', flex: 0.25, minWidth: 100, type: 'string' },
            { field: 'invoiceDate', headerName: 'Invoice Date', width: 200, type: 'date', valueFormatter: (value) => dateFormatter(value) },
            { field: 'customerName', headerName: 'Customer', flex: 0.30, minWidth: 300, type: 'string' },
            { field: 'eventName', headerName: 'Event Name', width: 500, type: 'string' },
            { field: 'venueName', headerName: 'Venue Name', width: 300, type: 'string' },
            { field: 'eventDate', headerName: 'Event Date', width: 200, type: 'date', valueFormatter: (value) => dateFormatter(value) },
            { field: 'section', headerName: 'Section', flex: 0.1, minWidth: 150, type: 'string' },
            { field: 'row', headerName: 'Row', flex: 0.1, minWidth: 150, type: 'string' },
            { field: 'seats', headerName: 'Seats', flex: 0.1, minWidth: 120, type: 'string' },
            { field: 'qty', headerName: 'Qty', flex: 0.1, minWidth: 100, type: 'number' },
            { field: 'costPerTicket', headerName: 'Cost/Ticket', flex: 0.12, minWidth: 100, type: 'number', valueFormatter: currencyFormatter },
            { field: 'totalCost', headerName: 'Total Cost', flex: 0.12, minWidth: 100, type: 'number', valueFormatter: currencyFormatter },
            { field: 'invoiceTotal', headerName: 'Invoice Total', flex: 0.12, minWidth: 100, type: 'number', valueFormatter: currencyFormatter },
            { field: 'feeRevenue', headerName: 'Fee (Revenue)', minWidth: 140, type: 'string' },
            { field: 'feeProfit', headerName: 'Fee (Profit)', minWidth: 140, type: 'string' },
            { field: 'payout', headerName: 'Payout', flex: 0.12, minWidth: 100, type: 'number', valueFormatter: currencyFormatter },
            { field: 'delivery', headerName: 'Delivery', flex: 0.12, minWidth: 100, type: 'string' },
            { field: 'ticketType', headerName: 'Ticket Type', flex: 0.12, minWidth: 100, type: 'string' },
            { field: 'paymentStatus', headerName: 'Payment Status', flex: 0.12, minWidth: 150, type: 'string' },
            { field: 'payoutDate', headerName: 'Payout Date', flex: 0.12, minWidth: 200, type: 'date', valueFormatter: (value) => dateFormatter(value) },
        ], []
    );

    return (
        <Box sx={{ p: 3 }}>
            <div style={{ height: '80vh', width: '100%' }}>
                <DataGridPro
                    apiRef={apiRef}
                    onFilterModelChange={() => queueMicrotask(calculateFooterTotals)}
                    onSortModelChange={() => queueMicrotask(calculateFooterTotals)}
                    rows={invoiceRows}
                    columns={columns}
                    loading={isLoading}
                    pagination
                    slots={{
                        toolbar: GridToolbar,
                        loadingOverlay: () => (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )
                    }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500
                            }
                        }
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 2,
                        gap: 4,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box>
                        <strong>Total Cost:</strong> {currencyFormatter(footerTotals.totalCost)}
                    </Box>

                    <Box>
                        <strong>Invoice Total:</strong> {currencyFormatter(footerTotals.invoiceTotal)}
                    </Box>

                    <Box>
                        <strong>Payout Total:</strong> {currencyFormatter(footerTotals.payout)}
                    </Box>

                    <Box>
                        <strong>Fee (Revenue):</strong> {currencyFormatter(footerTotals.feeRevenue)}
                    </Box>

                    <Box>
                        <strong>Fee (Profit):</strong> {currencyFormatter(footerTotals.feeProfit)}
                    </Box>
                </Box>
            </div>
        </Box>
    );
};

export default InvoiceReport;